import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, EchecApi } from '../partage/api/client';
import type { ReglagesDesNotifications } from '../partage/api/types';
import { Interrupteur, LigneQuiOuvre, ListeDeChoix } from '../partage/PiecesDuCarnet';
import { cles } from '../partage/relectures';
import {
  ABONNEMENT_PAS_FAIT,
  ENVOYER_UN_ESSAI,
  ESSAI_EN_ROUTE,
  GESTE_SANS_RESEAU,
  LA_NUIT,
  LA_NUIT_COMMENCE,
  LA_NUIT_FINIT,
  LA_FIN_D_UN_RENDEZ_VOUS,
  LES_RAPPELS_DU_JOUR_A,
  RAPPELS_HORS_DE_LA_NUIT,
  UNE_ATTENTE_QUI_DURE_TROP,
  UNE_ECHEANCE_PROCHE,
  laNuitLaisseLesRappels,
  PAS_DE_RESEAU,
  REGLAGE_PAS_ENREGISTRE,
  POUR_TOUS_TES_APPAREILS,
  RECEVOIR_SUR_CE_TELEPHONE,
  RIEN_NE_PART_LA_NUIT,
  UN_RENDEZ_VOUS_1H_AVANT,
} from '../partage/vocabulaire';
import { abonnementDeCetAppareil, desabonnerCetAppareil } from '../partage/abonnementDeCetAppareil';
import { abonnerCetAppareil, permissionDeCetAppareil } from './cetAppareil';
import {
  etatDeCetAppareil,
  heureDeLaNuit,
  heuresDeLaNuit,
  heuresDesRappels,
  motDeLaNuit,
  notificationsPossibles,
  phraseDeLEssai,
  phraseDeLEtat,
} from './notificationsDuTelephone';
import './PanneauDesNotifications.css';

/**
 * Le panneau Notifications des Paramètres du téléphone (chantier notifications-socle, ticket 03),
 * dans l'habit du téléphone : des interrupteurs et une ligne qui ouvre sa liste, jamais de
 * pastilles (`partage/PiecesDuCarnet.tsx`).
 *
 * En tête, ce téléphone : l'interrupteur dit l'abonnement présent dans son navigateur, ou une
 * phrase à sa place quand il ne peut rien répondre ; l'essai, quand il est abonné. Dessous, les
 * réglages du compte, pour tous ses appareils, lus même sans abonnement.
 */
export function PanneauDesNotifications() {
  const possible = notificationsPossibles(window);
  // `null` : l'abonnement du navigateur n'est pas encore lu ; rien ne se montre d'ici là.
  const [abonne, setAbonne] = useState<boolean | null>(possible ? null : false);
  const [permission, setPermission] = useState(permissionDeCetAppareil);
  const [enLigne, setEnLigne] = useState(() => navigator.onLine);
  const [enCours, setEnCours] = useState(false);
  const [mot, setMot] = useState<string | null>(null);

  useEffect(() => {
    let vivant = true;
    void abonnementDeCetAppareil().then((a) => vivant && setAbonne(a !== null));
    const enLigneOuNon = () => setEnLigne(navigator.onLine);
    window.addEventListener('online', enLigneOuNon);
    window.addEventListener('offline', enLigneOuNon);
    return () => {
      vivant = false;
      window.removeEventListener('online', enLigneOuNon);
      window.removeEventListener('offline', enLigneOuNon);
    };
  }, []);

  const etat = etatDeCetAppareil({ possible, permission, enLigne, abonne: abonne === true });
  const phrase = phraseDeLEtat(etat);

  const basculer = async (oui: boolean) => {
    setEnCours(true);
    setMot(null);
    if (oui) {
      const issue = await abonnerCetAppareil();
      setPermission(permissionDeCetAppareil());
      setAbonne(issue === 'abonne');
      if (issue === 'hors-ligne') setMot(PAS_DE_RESEAU);
      else if (issue === 'echec' || issue === 'impossible') setMot(ABONNEMENT_PAS_FAIT);
    } else {
      await desabonnerCetAppareil();
      setAbonne((await abonnementDeCetAppareil()) !== null);
    }
    setEnCours(false);
  };

  const essayer = async () => {
    setEnCours(true);
    setMot(ESSAI_EN_ROUTE);
    const abonnement = await abonnementDeCetAppareil();
    try {
      if (!abonnement) throw new Error('plus abonné');
      const { envoi } = await api.essaiDeNotification(abonnement.endpoint);
      setMot(phraseDeLEssai(envoi));
      // Le service du téléphone ne connaît plus cet abonnement : le serveur l'a effacé, le navigateur suit.
      if (envoi === 'abonnement_perdu') {
        await abonnement.unsubscribe().catch(() => false);
        setAbonne(false);
      }
    } catch {
      setMot(phraseDeLEssai('echec'));
    }
    setEnCours(false);
  };

  return (
    <div className="notifications-tel">
      {phrase !== null ? (
        <p className="notifications-tel-phrase">{phrase}</p>
      ) : (
        abonne !== null && (
          <div className={enCours ? 'notifications-tel-en-cours' : undefined} aria-busy={enCours}>
            <Interrupteur titre={RECEVOIR_SUR_CE_TELEPHONE} oui={abonne} onBasculer={(oui) => !enCours && void basculer(oui)} />
            {abonne && (
              <button type="button" className="pouce-ligne-reg" disabled={enCours} onClick={() => void essayer()}>
                {ENVOYER_UN_ESSAI}
              </button>
            )}
          </div>
        )
      )}
      {mot && (
        <p className="notifications-tel-mot" role="status">
          {mot}
        </p>
      )}

      <ReglagesDuCompte />
    </div>
  );
}

/**
 * Ce qui est ouvert : la liste des rappels du jour, ou sous « La nuit » la ligne elle-même, puis la
 * liste d'une de ses bornes.
 */
type Ouvert = null | 'rappels' | 'nuit' | 'debut' | 'fin';

/**
 * Les réglages du compte : un interrupteur par sorte, l'heure des rappels du jour, la nuit.
 * Enregistrés au serveur à chaque geste. Les listes d'heures ne proposent que ce que le serveur
 * accepte (`heuresDeLaNuit`, `heuresDesRappels`) : une nuit qui dure, les rappels hors de la nuit.
 */
function ReglagesDuCompte() {
  const client = useQueryClient();
  const reglages = useQuery({ queryKey: cles.reglagesDesNotifications, queryFn: api.reglagesDesNotifications });
  const [ouvert, setOuvert] = useState<Ouvert>(null);
  const [pasEnregistre, setPasEnregistre] = useState<string | null>(null);

  const ecrire = useMutation({
    mutationFn: api.ecrireReglagesDesNotifications,
    onMutate: (nouveaux: ReglagesDesNotifications) => {
      setPasEnregistre(null);
      client.setQueryData(cles.reglagesDesNotifications, nouveaux);
    },
    onSuccess: (lus) => client.setQueryData(cles.reglagesDesNotifications, lus),
    onError: (erreur) => {
      setPasEnregistre(erreur instanceof EchecApi && erreur.code === 'hors_ligne' ? GESTE_SANS_RESEAU : REGLAGE_PAS_ENREGISTRE);
      void client.invalidateQueries({ queryKey: cles.reglagesDesNotifications });
    },
  });

  const r = reglages.data;
  if (!r) return null;
  const changer = (changement: Partial<ReglagesDesNotifications>) => ecrire.mutate({ ...r, ...changement });

  return (
    <div className="notifications-tel-reglages">
      <p className="pouce-rg-t">{POUR_TOUS_TES_APPAREILS}</p>
      <Interrupteur
        titre={UN_RENDEZ_VOUS_1H_AVANT}
        oui={r.rendezVousUneHeureAvant}
        onBasculer={(oui) => changer({ rendezVousUneHeureAvant: oui })}
      />
      <Interrupteur titre={LA_FIN_D_UN_RENDEZ_VOUS} oui={r.finDeRendezVous} onBasculer={(oui) => changer({ finDeRendezVous: oui })} />
      <Interrupteur titre={UNE_ECHEANCE_PROCHE} oui={r.echeanceProche} onBasculer={(oui) => changer({ echeanceProche: oui })} />
      <Interrupteur
        titre={UNE_ATTENTE_QUI_DURE_TROP}
        oui={r.attenteQuiDureTrop}
        onBasculer={(oui) => changer({ attenteQuiDureTrop: oui })}
      />
      <LigneQuiOuvre
        titre={LES_RAPPELS_DU_JOUR_A}
        valeur={heureDeLaNuit(r.heureDesRappels)}
        ouverte={ouvert === 'rappels'}
        onOuvrir={() => setOuvert(ouvert === 'rappels' ? null : 'rappels')}
        attributs={{ 'data-reglage': 'rappels' }}
      />
      {ouvert === 'rappels' && (
        <div className="notifications-tel-rappels">
          <p className="notifications-tel-sous">{RAPPELS_HORS_DE_LA_NUIT}</p>
          <ListeDeChoix
            nom={LES_RAPPELS_DU_JOUR_A}
            choix={heuresDesRappels(r).map((h) => [h, heureDeLaNuit(h)] as const)}
            choisi={r.heureDesRappels}
            attribut="heure"
            onChoisir={(h) => {
              changer({ heureDesRappels: h });
              setOuvert(null);
            }}
          />
        </div>
      )}
      <LigneQuiOuvre
        titre={LA_NUIT}
        valeur={motDeLaNuit(r.nuitDebut, r.nuitFin)}
        ouverte={ouvert === 'nuit' || ouvert === 'debut' || ouvert === 'fin'}
        onOuvrir={() => setOuvert(ouvert === null || ouvert === 'rappels' ? 'nuit' : null)}
        attributs={{ 'data-reglage': 'nuit' }}
      />
      {(ouvert === 'nuit' || ouvert === 'debut' || ouvert === 'fin') && (
        <div className="notifications-tel-nuit">
          <p className="notifications-tel-sous">
            {RIEN_NE_PART_LA_NUIT} {laNuitLaisseLesRappels(heureDeLaNuit(r.heureDesRappels))}
          </p>
          <LigneQuiOuvre
            titre={LA_NUIT_COMMENCE}
            valeur={heureDeLaNuit(r.nuitDebut)}
            ouverte={ouvert === 'debut'}
            onOuvrir={() => setOuvert(ouvert === 'debut' ? 'nuit' : 'debut')}
            attributs={{ 'data-reglage': 'nuit-debut' }}
          />
          {ouvert === 'debut' && (
            <ListeDeChoix
              nom={LA_NUIT_COMMENCE}
              choix={heuresDeLaNuit('debut', r).map((h) => [h, heureDeLaNuit(h)] as const)}
              choisi={r.nuitDebut}
              attribut="heure"
              onChoisir={(h) => {
                changer({ nuitDebut: h });
                setOuvert('nuit');
              }}
            />
          )}
          <LigneQuiOuvre
            titre={LA_NUIT_FINIT}
            valeur={heureDeLaNuit(r.nuitFin)}
            ouverte={ouvert === 'fin'}
            onOuvrir={() => setOuvert(ouvert === 'fin' ? 'nuit' : 'fin')}
            attributs={{ 'data-reglage': 'nuit-fin' }}
          />
          {ouvert === 'fin' && (
            <ListeDeChoix
              nom={LA_NUIT_FINIT}
              choix={heuresDeLaNuit('fin', r).map((h) => [h, heureDeLaNuit(h)] as const)}
              choisi={r.nuitFin}
              attribut="heure"
              onChoisir={(h) => {
                changer({ nuitFin: h });
                setOuvert('nuit');
              }}
            />
          )}
        </div>
      )}
      {pasEnregistre && (
        <p className="notifications-tel-mot" role="status">
          {pasEnregistre}
        </p>
      )}
    </div>
  );
}
